import React, { useCallback, useEffect, useState } from 'react';
import { CONTENT_TOP } from 'dashboard/layouts/admin/chromeGeometry';
import { Box, Stack } from '@mui/material';
import { useOutletContext } from 'react-router';
import { toast } from 'react-toastify';
import axios from 'axios';
import { API } from 'utilities/api';
import { backendURL } from 'config';
import { headerJson } from 'utilities/common';
import {
  EmptyState, PageHeader, PagePaper,
} from 'dashboard/components/common';
import CustomerDialog  from './CustomerDialog';
import BroadcastDialog from './BroadcastDialog';
import AudienceKpiRow  from './AudienceKpiRow';
import AudienceToolbar from './AudienceToolbar';
import AudienceTable   from './AudienceTable';

/**
 * Per-brand audience.
 *
 * Each customer profile is unique to a brand even when the same email
 * logs in elsewhere. Owners can edit one customer at a time, run a
 * marketing broadcast, or export the list as CSV. KPI row, toolbar,
 * and table each live in their own files.
 */
export default function Audience() {
  const [selectedBrand] = useOutletContext();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [broadcastOpen, setBroadcastOpen] = useState(false);

  const reload = useCallback(() => {
    if (!selectedBrand) return Promise.resolve();
    setLoading(true);
    return API.listBrandCustomers({ brand_id: selectedBrand, search })
      .then((res) => setRows(Array.isArray(res) ? res : []))
      .catch((err) => toast.error(err?.message || 'Failed to load customers', { toastId: 'audience-load-error' }))
      .finally(() => setLoading(false));
  }, [selectedBrand, search]);

  useEffect(() => {
    if (!selectedBrand) return undefined;
    let cancelled = false;
    setLoading(true);
    API.listBrandCustomers({ brand_id: selectedBrand, search })
      .then((res) => { if (!cancelled) setRows(Array.isArray(res) ? res : []); })
      .catch((err) => { if (!cancelled) toast.error(err?.message || 'Failed to load customers', { toastId: 'audience-load-error' }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selectedBrand, search]);

  const exportCsv = async () => {
    try {
      const res = await axios.post(
        `${backendURL}/dashboard/brand/customers/export-csv`,
        { brand_id: selectedBrand, limit: 5000 },
        { headers: headerJson(), responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `customers-${String(selectedBrand).slice(-6)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Export failed');
    }
  };

  return (
    <Box sx={{ pt: CONTENT_TOP }}>
      <PagePaper>
        <PageHeader
          title='Audience'
          subtitle="Customers who signed up or signed in on this brand. Each customer's profile here is unique to this brand — even if they shop with you under another brand using the same email."
        />

        {!selectedBrand && <EmptyState>Select a brand to view its audience.</EmptyState>}

        {selectedBrand && (
          <Stack spacing='14px'>
            <AudienceKpiRow rows={rows} />
            <AudienceToolbar
              search={search}
              onSearchChange={setSearch}
              onRefresh={reload}
              onBroadcast={() => setBroadcastOpen(true)}
              onExport={exportCsv}
              rowsShown={rows.length}
              disabled={rows.length === 0}
            />
            <AudienceTable
              rows={rows}
              loading={loading}
              onEdit={(r) => { setEditingRow(r); setDlgOpen(true); }}
            />
          </Stack>
        )}
      </PagePaper>

      <CustomerDialog
        open={dlgOpen}
        onClose={() => setDlgOpen(false)}
        brandId={selectedBrand}
        row={editingRow}
        onSaved={reload}
      />

      <BroadcastDialog
        open={broadcastOpen}
        onClose={() => setBroadcastOpen(false)}
        brandId={selectedBrand}
      />
    </Box>
  );
}
